/**
 * What the engine's templates are called, and which shelf of the library each
 * one sits on.
 *
 * The labels used to live inside the page component; the library, the
 * schedules view, the delivery rows and the detail page all need them, so they
 * are one table here.
 */
import type { ReportTypeId } from '@/services/reportDefinitions'
import { reportTypeSpec } from '../reportTypes'

export type TypeBucketKey = 'pip' | 'cmb' | 'scr' | 'dos' | 'amr' | 'prt'

export type TypeBucket = { key: TypeBucketKey; code: string; name: string }

/** The shelves, in the order the library lists them. */
export const TYPE_BUCKETS: TypeBucket[] = [
  { key: 'pip', code: 'PIP', name: 'Pipeline' },
  { key: 'cmb', code: 'CMB', name: 'Committee Book' },
  { key: 'scr', code: 'SCR', name: 'Screener' },
  { key: 'dos', code: 'DOS', name: 'Deal documents' },
  { key: 'amr', code: 'AMR', name: 'Asset Management' },
  { key: 'prt', code: 'PRT', name: 'Portfolio and risk' },
]

const BUCKET_BY_KEY = Object.fromEntries(TYPE_BUCKETS.map((bucket) => [bucket.key, bucket])) as Record<
  TypeBucketKey,
  TypeBucket
>

/** Report-engine templates, for files and schedules that did not come from a saved definition. */
export const TEMPLATE_LABELS: Record<string, { name: string; bucket: TypeBucketKey }> = {
  deal_package: { name: 'Deal Dossier', bucket: 'dos' },
  // Owner decision #2 (25 Sep 2026): the full document is presented as
  // "Complete Deal Lifecycle"; its short form as "Executive Summary".
  deal_dossier: { name: 'Complete Deal Lifecycle', bucket: 'dos' },
  deal_dossier_summary: { name: 'Executive Summary', bucket: 'dos' },
  term_sheet: { name: 'Term Sheet Summary', bucket: 'dos' },
  deal_template: { name: 'Deal Excel Workbook', bucket: 'dos' },
  underwriting: { name: 'Underwriting Report', bucket: 'dos' },
  portfolio: { name: 'Portfolio Summary', bucket: 'prt' },
  surveillance: { name: 'Surveillance Report', bucket: 'prt' },
  pipeline_summary: { name: 'Pipeline Report', bucket: 'pip' },
  pipeline_backshop: { name: 'Pipeline Report (Backshop layout)', bucket: 'pip' },
  current_status: { name: 'Current Status', bucket: 'pip' },
  pipeline_changes: { name: 'Pipeline Changes', bucket: 'pip' },
  pipeline_daily: { name: 'Daily Pipeline', bucket: 'pip' },
  field_export: { name: 'Field Export', bucket: 'pip' },
  committee_book: { name: 'Committee Book', bucket: 'cmb' },
  screener: { name: 'Screener', bucket: 'scr' },
  asset_management: { name: 'Asset Management Report', bucket: 'amr' },
}

export function displayTemplate(template: string): string {
  return TEMPLATE_LABELS[template]?.name ?? template.replace(/_/g, ' ')
}

export function bucketOfTemplate(template: string): TypeBucket {
  return BUCKET_BY_KEY[TEMPLATE_LABELS[template]?.bucket ?? 'prt']
}

/** The engine template a saved definition renders with. */
export function engineTemplateOf(reportType: ReportTypeId): string {
  return reportTypeSpec(reportType).engineTemplate
}

/** The shelf a saved definition sits on, by what it prints. */
export function bucketOfDefinitionType(reportType: ReportTypeId): TypeBucket {
  return bucketOfTemplate(engineTemplateOf(reportType))
}

/** Lower-cased, punctuation-free, for comparing a title with a name. */
export function normalizeName(value: string | null | undefined): string {
  return String(value ?? '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, ' ')
    .trim()
}
